import { createAsyncThunk } from '@reduxjs/toolkit';
import { isNil, noop } from 'lodash';
import ServiceContainer from 'src/ServiceContainer';
import { AppState, AppThunkDispatch } from 'src/store';
import { AppType } from 'configuration-schemas/general';
import { logError } from 'src/services/loggingService';
import { getValidValues } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import { getUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { Option as DropdownOption } from 'src/components/Subheader/Subheader';
import type { AttributeBlacklistConfig } from 'configuration-schemas/viewdefns/general';
import { zAttributeBlacklistConfig } from 'configuration-schemas/viewdefns/general';
import { setExtraPivotParamsOptions, setExtraPivotParamsSelection, setAttributeBlacklist } from './Subheader.slice';
import { AttributeBlacklistDefnId } from 'src/utils/Component/AttributeBlacklist.utils';

/**
 * Loads the attribute blacklist viewdefn and stores it on the subheader slice.
 * Fired after the navshell mounts.
 */
export const loadAttributeBlacklist = () => async (dispatch: AppThunkDispatch) => {
  try {
    const configs = await ServiceContainer.tenantConfigClient.getTenantViewDefns<AttributeBlacklistConfig>({
      defnIds: [AttributeBlacklistDefnId],
      appName: AppType.Assortment,
      validationSchemas: [zAttributeBlacklistConfig],
    });
    dispatch(setAttributeBlacklist(configs[0]));
  } catch (_err) {
    noop();
  }
};

export const fetchExtraPivotParamsOptions = createAsyncThunk<void, void, { state: AppState }>(
  'subheader/fetchExtraPivotParamsOptions',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { extraPivotParamsConfig, extraPivotParamsSelections } = state.subheader;

    if (!extraPivotParamsConfig) {
      // Safety check
      return;
    }

    type OptionsResult = { key: string; options: DropdownOption[] };

    const promises: Promise<OptionsResult>[] = Object.keys(extraPivotParamsConfig).map(
      async (key): Promise<OptionsResult> => {
        const param = extraPivotParamsConfig[key];
        const dataApi = param.dataApi;

        try {
          if (dataApi.isListData) {
            // Call listData API
            const res = await ServiceContainer.pivotService.listData(dataApi.defnId, AppType.Assortment, {
              ...dataApi.params,
            });
            const options: DropdownOption[] = res.flat.map((item: any) => ({
              ...item,
              text: item.label, // Add 'text' for SubheaderDropdown
            }));
            return { key, options };
          } else {
            // Handle non-listData APIs
            const values = await getValidValues(getUrl(dataApi));
            const options: DropdownOption[] = values.map((v: any) => ({
              value: v.value,
              label: v.label,
              text: v.label, // Add 'text' for SubheaderDropdown
            }));
            return { key, options };
          }
        } catch (error) {
          logError(`Failed to fetch options for ${key}:`, error);
          return { key, options: [] };
        }
      },
    );

    const results = await Promise.all(promises);
    // Build a map of key to options
    const optionsMap: Record<string, DropdownOption[]> = {};
    results.forEach(({ key, options }) => {
      optionsMap[key] = options;
    });

    // Set all the new options all at once
    dispatch(setExtraPivotParamsOptions(optionsMap));

    // Now, update the selections based on the new options, leaving the pre-selected ones intact
    const updatedSelections: Record<string, string | number | null> = { ...extraPivotParamsSelections };

    Object.keys(optionsMap).forEach((key) => {
      const options = optionsMap[key];
      const currentSelectionValue = extraPivotParamsSelections[key];
      let newSelectionValue: string | number | null = null;

      if (options && options.length > 0) {
        const values = options.map((option) => option.value);

        // Check if current selection value exists in new options values
        if (!isNil(currentSelectionValue) && values.includes(currentSelectionValue)) {
          // Current selection value is valid, keep it
          newSelectionValue = currentSelectionValue;
        } else {
          // Default to first valid option's value
          newSelectionValue = values[0] ?? null;
        }
      } else {
        // No options available, set selection to null - this shouldn't get hit, unless the pivot returns empty
        newSelectionValue = null;
      }

      updatedSelections[key] = newSelectionValue;
    });

    // Dispatch updated selections only for keys in optionsMap, thanks stack overflow
    Object.entries(updatedSelections).forEach(([key, value]) => {
      if (optionsMap.hasOwnProperty(key)) {
        dispatch(setExtraPivotParamsSelection({ key, value }));
      }
    });
  },
);
